import { useMemo } from 'react';
import { css, CSSObject } from '@emotion/react';
import { ErrorDetails } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v2/insurance_verification_adapter.pb';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { BannerNotification, Text, Button } from '@frontend/design-system';
import { getErrorMessageFromDentalCoverageErrorInfo, toTitleCase } from '../utils/utils';

interface Props {
  errorDetails?: ErrorDetails;
  customMessage?: string;
  isRetryButtonVisible?: boolean;
  customCss?: CSSObject;
  onRetryClick?: () => void;
}

export const ErrorBanner = ({ isRetryButtonVisible, errorDetails, customMessage, customCss, onRetryClick }: Props) => {
  const { t } = useTranslation('insuranceDetails');

  const { errorMessage, errorList } = useMemo(
    () => getErrorMessageFromDentalCoverageErrorInfo(errorDetails),
    [errorDetails]
  );

  if (!errorDetails && !customMessage) {
    return null;
  }

  const hasAPIError = !!errorDetails;
  const hasErrorList = errorList.length > 0;
  const hasSingleErrorInErrorList = hasErrorList && errorList.length === 1;
  const hasSameSingleErrrorInErrorList =
    hasSingleErrorInErrorList &&
    toTitleCase(errorMessage)?.toLowerCase() === toTitleCase(errorList?.[0])?.toLowerCase();
  const hasErrorMessage = !!errorMessage;
  // @note: We only want to show the error message if there is a single error in the error list and it is not the same as the error message. This is to avoid showing duplicate error messages.
  const shouldShowErrorMessage =
    hasErrorMessage && hasErrorList && hasSingleErrorInErrorList && !hasSameSingleErrrorInErrorList;
  return (
    <BannerNotification
      status='error'
      css={{ margin: theme.spacing(2, 0), ...customCss }}
      message={
        <div>
          {shouldShowErrorMessage && (
            <Text size='medium' css={{ marginBottom: theme.spacing(1) }}>
              {toTitleCase(errorMessage)}
            </Text>
          )}
          {hasAPIError && (
            <>
              <Text size='large' weight='bold'>
                {t('Failure Details')}
              </Text>
              {hasErrorList ? (
                <ul css={{ paddingLeft: theme.spacing(1.5) }}>
                  {errorList.map((error, index) => (
                    <Text as='li' key={index} size='medium'>
                      {toTitleCase(error)}
                    </Text>
                  ))}
                </ul>
              ) : (
                <Text size='medium'>
                  {toTitleCase(errorMessage) || t('Something went wrong! Please try again later.')}{' '}
                </Text>
              )}
            </>
          )}
          {isRetryButtonVisible && (
            <Button variant='tertiary' css={retryButtonStyle} onClick={onRetryClick}>
              {t('Try Again')}
            </Button>
          )}
        </div>
      }
    />
  );
};

const retryButtonStyle = css({
  padding: theme.spacing(1, 0),
  width: 'auto',
  color: theme.colors.primary50,
  fontWeight: theme.font.weight.bold,
  fontSize: theme.fontSize(14),
  ':hover': { background: 'none' },
});
